import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PawPrint,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Mail,
  CalendarCheck,
  TrendingUp,
  Users,
  Wallet,
  ClipboardList,
  Send,
  Sparkles,
  Smartphone,
  KanbanSquare,
  BarChart3,
  Star,
  ChevronDown,
  Quote,
  Trophy,
  Clock,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BackToTop from "@/components/BackToTop";
import ContactEmailBlock from "@/components/ContactEmailBlock";

const FRAUNCES = '"Fraunces", ui-serif, Georgia, serif';

export default function LandingPage() {
  useEffect(() => {
    document.title = "Run your dog training business, not your inbox";
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#fdf8f3] text-slate-900 antialiased">
      <Nav />
      <Hero />
      <ProblemSolution />
      <KeyFeatures />
      <PipelineShowcase />
      <AnalyticsShowcase />
      <RemindersShowcase />
      <MobileWorkflow />
      <Testimonials />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
      <BackToTop />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`px-5 py-16 md:px-8 md:py-24 ${className}`}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
      {children}
    </p>
  );
}

function Headline({ children, className = "" }) {
  return (
    <h2
      className={`text-3xl font-medium leading-[1.05] tracking-tight md:text-5xl ${className}`}
      style={{ fontFamily: FRAUNCES }}
    >
      {children}
    </h2>
  );
}

/* -------------------------------------------------------------------------- */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition ${
        scrolled ? "border-b border-slate-200/70 bg-[#fdf8f3]/85 backdrop-blur" : ""
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3 md:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white">
            <PawPrint size={18} />
          </div>
          <span className="text-base font-semibold tracking-tight">Pawbook</span>
        </Link>
        <nav className="hidden items-center gap-7 text-sm text-slate-700 md:flex">
          <a href="#features" className="hover:text-slate-900">Features</a>
          <a href="#pipeline" className="hover:text-slate-900">Pipeline</a>
          <a href="#pricing" className="hover:text-slate-900">Pricing</a>
          <a href="#faq" className="hover:text-slate-900">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/intake" className="hidden text-sm font-medium text-slate-700 hover:text-slate-900 sm:inline" data-testid="nav-intake">
            For dog owners
          </Link>
          <Link to="/login" data-testid="nav-login">
            <Button variant="outline" className="rounded-full px-4">
              Login
            </Button>
          </Link>
          <Link to="/demo" className="hidden sm:inline" data-testid="nav-demo">
            <Button className="rounded-full bg-slate-900 px-4 hover:bg-slate-800">
              Try the demo
              <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft warm radial behind hero */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(80% 50% at 30% 0%, rgba(251,191,36,0.18), transparent 60%), radial-gradient(60% 40% at 90% 30%, rgba(249,115,22,0.10), transparent 60%)",
        }}
      />
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-5 py-12 sm:py-16 md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:gap-12 md:px-8 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="min-w-0"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900">
            <Sparkles size={12} /> Built for working trainers
          </div>
          <h1
            className="text-[2rem] font-medium leading-[1.08] tracking-tight text-slate-900 [overflow-wrap:break-word] [text-wrap:balance] sm:text-5xl sm:leading-[1.05] md:text-7xl md:leading-[1.02]"
            style={{ fontFamily: FRAUNCES }}
            data-testid="hero-headline"
          >
            Run your training business,{" "}
            <span className="italic text-amber-700" style={{ fontFeatureSettings: '"ss01"' }}>
              not your inbox
            </span>
            .
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:mt-6 sm:text-lg">
            The all-in-one client manager, lead pipeline, and automation hub
            built for solo dog trainers and small training teams. Turn intake
            forms into paying clients on autopilot.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-2.5 sm:mt-8 sm:gap-3" data-testid="hero-ctas">
            <Link to="/demo">
              <Button className="rounded-full bg-slate-900 px-5 py-5 text-sm hover:bg-slate-800 sm:px-6 sm:py-6 sm:text-base" data-testid="hero-try-demo">
                Try the live demo
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href="#cta">
              <Button className="rounded-full bg-amber-600 px-5 py-5 text-sm text-white hover:bg-amber-700 sm:px-6 sm:py-6 sm:text-base" data-testid="hero-book-demo">
                Book a setup call
              </Button>
            </a>
            <Link to="/intake">
              <Button variant="outline" className="rounded-full px-5 py-5 text-sm sm:px-6 sm:py-6 sm:text-base" data-testid="hero-request-consult">
                For dog owners
              </Button>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500 sm:mt-8 sm:gap-5 sm:text-sm">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> No signup required</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> Sandboxed sample data</span>
            <span className="hidden items-center gap-1.5 md:flex"><CheckCircle2 size={14} className="text-emerald-600" /> Setup in minutes</span>
          </div>
        </motion.div>

        {/* Dashboard preview mock */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative min-w-0"
        >
          <DashboardMock />
        </motion.div>
      </div>
    </section>
  );
}

function DashboardMock() {
  return (
    <div className="relative rounded-3xl border border-slate-200/70 bg-white p-4 shadow-2xl shadow-amber-100/60">
      <div className="mb-3 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        <span className="ml-2 text-[10px] font-medium text-slate-400">app.pawbook.io</span>
      </div>
      <div className="grid gap-2 md:grid-cols-4">
        <MiniStat label="Active" value="12" tone="text-slate-900" />
        <MiniStat label="This week" value="7" tone="text-blue-700" />
        <MiniStat label="Won this mo" value="4" tone="text-emerald-700" />
        <MiniStat label="Balances" value="$2,150" tone="text-amber-700" />
      </div>
      <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Upcoming sessions
        </p>
        <div className="flex gap-2 overflow-hidden">
          <UpcomingCard tone="amber" when="Today" dog="Diesel" owner="Marcus G." />
          <UpcomingCard tone="blue" when="Tomorrow" dog="Bentley" owner="Sarah M." />
          <UpcomingCard tone="slate" when="In 3 days" dog="Cooper" owner="Maria S." />
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-2xl border border-slate-100 bg-white p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bentley</p>
          <p className="text-sm font-semibold text-slate-900">Active client</p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full w-3/4 rounded-full bg-emerald-500" />
          </div>
          <p className="mt-1 text-[10px] text-slate-500">Session 3 of 4 • $600 paid</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Diesel</p>
          <p className="text-sm font-semibold text-slate-900">Needs follow-up</p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full w-1/3 rounded-full bg-amber-500" />
          </div>
          <p className="mt-1 text-[10px] text-slate-500">Reactivity • $750 balance</p>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-3">
      <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-0.5 text-xl font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

function UpcomingCard({ tone, when, dog, owner }) {
  const styles = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    slate: "bg-white border-slate-200 text-slate-800",
  }[tone];
  return (
    <div className={`min-w-[110px] flex-1 rounded-xl border p-2 ${styles}`}>
      <p className="text-[9px] font-bold uppercase tracking-wider">{when}</p>
      <p className="mt-0.5 text-sm font-semibold">{dog}</p>
      <p className="text-[10px] opacity-80">{owner}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function ProblemSolution() {
  const problems = [
    "Leads slip through the cracks while you're on a session",
    "Reschedules and reminders eat your evenings",
    "Payment tracking lives in three different apps",
    "Owners forget homework between sessions",
    "You can't tell which marketing channel actually pays",
  ];
  return (
    <Section className="border-t border-slate-100">
      <div className="grid gap-12 md:grid-cols-2">
        <FadeIn>
          <Eyebrow>The trap most trainers fall into</Eyebrow>
          <Headline>
            Your <em className="italic">real</em> job isn't admin.
            <br />
            But somehow it became the job.
          </Headline>
          <ul className="mt-8 space-y-3">
            {problems.map((p) => (
              <li key={p} className="flex items-start gap-3 text-slate-700">
                <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">×</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-6 md:p-8">
            <Eyebrow>What you get instead</Eyebrow>
            <Headline className="text-emerald-900">
              One dashboard.
              <br />
              <em className="italic">Every lead handled.</em>
            </Headline>
            <ul className="mt-6 space-y-3 text-slate-800">
              {[
                ["Auto lead capture", "Public intake page + admin notifications"],
                ["Visual pipeline", "Drag leads through stages, track conversion"],
                ["Smart reminders", "Owners get session reminders & welcome emails"],
                ["Payment ledger", "Every dollar tracked, balances always current"],
                ["Real analytics", "See which sources actually convert"],
              ].map(([title, desc]) => (
                <li key={title} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                  <span>
                    <span className="font-semibold">{title}.</span>{" "}
                    <span className="text-slate-600">{desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </FadeIn>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
function KeyFeatures() {
  const features = [
    { icon: <ClipboardList size={20} />, title: "Client + dog profiles", desc: "Everything you need on every dog: goals, behavior notes, sessions, homework, payments." },
    { icon: <KanbanSquare size={20} />, title: "Lead pipeline", desc: "Drag leads through New → Contacted → Booked → Won. Status auto-syncs to client records." },
    { icon: <CalendarCheck size={20} />, title: "Session reminders", desc: "Automated email reminders 24 hours before each session. No more no-shows." },
    { icon: <Wallet size={20} />, title: "Payment ledger", desc: "Every payment logged with date, amount, balance before & after. Built-in CSV export." },
    { icon: <BarChart3 size={20} />, title: "Conversion analytics", desc: "Avg time-in-stage, win rate by source, fastest & slowest conversion paths." },
    { icon: <Users size={20} />, title: "Staff + roles", desc: "Add trainers and front-desk staff with role-specific permissions. Built for teams of 1 to 12." },
  ];
  return (
    <Section id="features" className="bg-white">
      <FadeIn>
        <Eyebrow>What's inside</Eyebrow>
        <Headline className="max-w-2xl">
          Everything a working trainer needs.
          <br />
          <em className="italic text-slate-500">Nothing they don't.</em>
        </Headline>
      </FadeIn>
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <FadeIn key={f.title} delay={i * 0.04}>
            <div className="group h-full rounded-3xl border border-slate-200 bg-white p-6 transition hover:border-slate-300 hover:shadow-sm">
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-amber-200">
                {f.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
function PipelineShowcase() {
  return (
    <Section id="pipeline" className="border-t border-slate-100">
      <div className="grid gap-12 md:grid-cols-[1fr_1.1fr] md:items-center">
        <FadeIn>
          <Eyebrow>Lead pipeline</Eyebrow>
          <Headline>
            See every lead.
            <br />
            <em className="italic text-amber-700">Lose none of them.</em>
          </Headline>
          <p className="mt-5 max-w-md text-slate-600">
            A live kanban board pulls every inquiry — from your public intake
            form, walk-ins, or referrals — into a six-stage pipeline. Drag
            cards forward as you make progress, and the client record updates
            automatically.
          </p>
          <ul className="mt-6 space-y-2 text-sm">
            {[
              "Public intake form lands as a new lead",
              "Auto-notifies you when a request arrives",
              "Drag cards forward as you call, book, win",
              "Stage history audit per lead — who moved them, when",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2 text-slate-700">
                <ArrowUpRight size={14} className="mt-1 flex-shrink-0 text-amber-600" />
                {b}
              </li>
            ))}
          </ul>
        </FadeIn>
        <FadeIn delay={0.1}>
          <KanbanMock />
        </FadeIn>
      </div>
    </Section>
  );
}

function KanbanMock() {
  const cols = [
    { name: "New Lead", dot: "bg-blue-500", count: 4, cards: [{ dog: "Maya", owner: "Liz P.", issue: "Pulling, jumping" }] },
    { name: "Contacted", dot: "bg-indigo-500", count: 2, cards: [{ dog: "Hugo", owner: "Dan W.", issue: "Reactivity" }] },
    { name: "Booked", dot: "bg-violet-500", count: 3, cards: [{ dog: "Pepper", owner: "Anna T.", issue: "Puppy 101" }] },
    { name: "Won", dot: "bg-emerald-500", count: 8, cards: [{ dog: "Bentley", owner: "Sarah M.", issue: "Loose-leash" }] },
  ];
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/40">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {cols.map((c) => (
          <div key={c.name} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-900">
                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                {c.name}
              </span>
              <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                {c.count}
              </span>
            </div>
            {c.cards.map((card) => (
              <div key={card.dog} className="rounded-xl border border-slate-200 bg-white p-2">
                <p className="text-xs font-bold text-slate-900">{card.dog}</p>
                <p className="text-[10px] text-slate-600">{card.owner}</p>
                <p className="mt-1 line-clamp-2 text-[10px] text-slate-500">{card.issue}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function AnalyticsShowcase() {
  return (
    <Section className="bg-white">
      <div className="grid gap-12 md:grid-cols-[1.1fr_1fr] md:items-center">
        <FadeIn>
          <AnalyticsMock />
        </FadeIn>
        <FadeIn delay={0.1}>
          <Eyebrow>Conversion analytics</Eyebrow>
          <Headline>
            Find out what's
            <br />
            <em className="italic text-amber-700">actually working.</em>
          </Headline>
          <p className="mt-5 max-w-md text-slate-600">
            Average time-in-stage, win rate by source, fastest and slowest
            conversion paths — all derived from the data you're already
            entering. Stop guessing which marketing pays back.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
            <Stat label="Faster intake → won" value="2.3×" tone="text-amber-700" />
            <Stat label="Reduced no-shows" value="-71%" tone="text-emerald-700" />
          </div>
          <p className="mt-3 text-xs text-slate-400">Numbers based on early beta usage; your results will vary.</p>
        </FadeIn>
      </div>
    </Section>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className={`text-3xl font-bold tabular-nums ${tone}`}>{value}</p>
      <p className="mt-1 text-xs text-slate-600">{label}</p>
    </div>
  );
}

function AnalyticsMock() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/40">
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Leads", value: "12", delta: "+3", tone: "text-emerald-700" },
          { label: "Won", value: "4", delta: "+2", tone: "text-emerald-700" },
          { label: "Win rate", value: "33%", delta: "+12pts", tone: "text-emerald-700" },
        ].map((m) => (
          <div key={m.label} className="rounded-2xl border border-slate-100 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{m.label}</p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums text-slate-900">{m.value}</p>
            <p className={`flex items-center gap-1 text-[10px] font-medium ${m.tone}`}>
              <TrendingUp size={10} /> {m.delta} this month
            </p>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <BarChart3 size={12} /> Avg days per stage
        </p>
        {[
          { name: "New Lead", w: 18, color: "bg-blue-500" },
          { name: "Contacted", w: 38, color: "bg-indigo-500" },
          { name: "Booked", w: 64, color: "bg-violet-500" },
          { name: "Proposal", w: 28, color: "bg-amber-500" },
          { name: "Won", w: 92, color: "bg-emerald-500" },
        ].map((row) => (
          <div key={row.name} className="mb-1.5 grid grid-cols-[80px_1fr] items-center gap-2">
            <span className="text-[10px] text-slate-600">{row.name}</span>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${row.color}`} style={{ width: `${row.w}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function RemindersShowcase() {
  return (
    <Section className="border-t border-slate-100">
      <div className="grid gap-12 md:grid-cols-[1.1fr_1fr] md:items-center">
        <FadeIn>
          <Eyebrow>Automated comms</Eyebrow>
          <Headline>
            The owner gets the email.
            <br />
            <em className="italic text-amber-700">You get the time back.</em>
          </Headline>
          <p className="mt-5 max-w-md text-slate-600">
            New leads get a confirmation receipt. Booked clients get a session
            reminder 24 hours out. Won clients get a welcome message with
            their first homework. No copy-pasting from your notes app.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-slate-700">
            {[
              ["New inquiry", "Owner gets a polite receipt; you get a heads-up"],
              ["Session booked", "Owner gets a confirmation with the date"],
              ["Day before a session", "Owner gets a reminder + what to bring"],
              ["Welcomed as a client", "Owner gets a kickoff email with homework"],
            ].map(([trigger, what]) => (
              <li key={trigger} className="flex items-start gap-2">
                <Zap size={14} className="mt-1 flex-shrink-0 text-amber-600" />
                <span>
                  <span className="font-semibold">{trigger}.</span>{" "}
                  <span className="text-slate-600">{what}.</span>
                </span>
              </li>
            ))}
          </ul>
        </FadeIn>
        <FadeIn delay={0.1}>
          <EmailMock />
        </FadeIn>
      </div>
    </Section>
  );
}

function EmailMock() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/40">
      <div className="flex items-center gap-2 border-b border-slate-100 pb-2 text-xs text-slate-500">
        <Mail size={14} />
        <span className="font-medium text-slate-700">Reminder: training session for Bentley</span>
        <span className="ml-auto">Yesterday</span>
      </div>
      <div className="px-1 pt-3">
        <p className="text-xs text-slate-500">To: sarah@example.com</p>
        <p className="text-xs text-slate-500">From: reminders@yourbiz.com</p>
        <h4 className="mt-3 text-base font-semibold">Session reminder</h4>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          Hi Sarah, this is a friendly reminder that <strong>Bentley</strong> has a training session
          scheduled for <strong>Monday, May 18</strong>. Please bring high-value treats,
          your working harness, and any homework notes from last time.
        </p>
        <p className="mt-3 text-xs text-slate-500">— Your trainer</p>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          <CheckCircle2 size={14} /> Delivered automatically — you didn't lift a finger
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function MobileWorkflow() {
  return (
    <Section className="bg-white">
      <div className="grid gap-12 md:grid-cols-2 md:items-center">
        <FadeIn>
          <PhoneMock />
        </FadeIn>
        <FadeIn delay={0.1}>
          <Eyebrow>Field-ready</Eyebrow>
          <Headline>
            Update sessions
            <br />
            <em className="italic text-amber-700">between every drill.</em>
          </Headline>
          <p className="mt-5 max-w-md text-slate-600">
            Designed mobile-first from the start. Log session notes from your
            phone the moment they happen, record payments while standing in a
            backyard, and move leads through the pipeline from the parking lot.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><Smartphone size={14} className="mt-1 flex-shrink-0 text-amber-600" /> Touch-friendly drag, swipe, and select interactions</li>
            <li className="flex items-start gap-2"><Smartphone size={14} className="mt-1 flex-shrink-0 text-amber-600" /> Works on any browser — no app store install</li>
            <li className="flex items-start gap-2"><Smartphone size={14} className="mt-1 flex-shrink-0 text-amber-600" /> One-tap payment recording with running balance</li>
          </ul>
        </FadeIn>
      </div>
    </Section>
  );
}

function PhoneMock() {
  return (
    <div className="mx-auto max-w-[280px]">
      <div className="rounded-[2.2rem] border-[8px] border-slate-900 bg-white p-3 shadow-2xl shadow-slate-300/50">
        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bentley</p>
          <p className="text-base font-bold">Golden Retriever • 2y</p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <div className="rounded-lg bg-white p-2">
              <p className="text-[9px] text-slate-500">Balance</p>
              <p className="text-sm font-bold text-amber-700">$300</p>
            </div>
            <div className="rounded-lg bg-white p-2">
              <p className="text-[9px] text-slate-500">Paid</p>
              <p className="text-sm font-bold text-emerald-700">$600</p>
            </div>
          </div>
          <button className="mt-2 w-full rounded-lg bg-slate-900 px-2 py-2 text-[11px] font-medium text-white">
            <Wallet size={11} className="-mt-0.5 mr-1 inline" /> Record payment
          </button>
          <button className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-[11px] font-medium text-slate-700">
            <Send size={11} className="-mt-0.5 mr-1 inline" /> Send reminder
          </button>
        </div>
        <div className="mt-2 rounded-2xl border border-slate-100 p-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Session note</p>
          <p className="mt-1 text-[11px] text-slate-700">May 11 — Improved focus with food lure around distractions. Practice recall in fenced yard.</p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function Testimonials() {
  const items = [
    { quote: "I stopped losing leads. Period.", name: "Beta Trainer #1", role: "Suwanee, GA", stars: 5 },
    { quote: "Owners actually show up now that they get the reminder.", name: "Beta Trainer #2", role: "Athens, GA", stars: 5 },
    { quote: "The pipeline made me realize I was leaving money on the table.", name: "Beta Trainer #3", role: "Atlanta, GA", stars: 5 },
  ];
  return (
    <Section className="border-t border-slate-100">
      <FadeIn>
        <Eyebrow>Early beta feedback</Eyebrow>
        <Headline className="max-w-2xl">
          Real trainers, <em className="italic text-amber-700">real time saved.</em>
        </Headline>
      </FadeIn>
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {items.map((t, i) => (
          <FadeIn key={t.name} delay={i * 0.04}>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-6">
              <Quote size={20} className="text-amber-500" />
              <p className="mt-3 text-lg leading-snug text-slate-900" style={{ fontFamily: FRAUNCES }}>
                "{t.quote}"
              </p>
              <div className="mt-5 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-amber-200">
                  <PawPrint size={14} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
                <div className="ml-auto flex gap-0.5 text-amber-500">
                  {Array.from({ length: t.stars }).map((_, idx) => (
                    <Star key={`${t.name}-star-${idx}`} size={12} fill="currentColor" />
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-slate-400">Names anonymized during private beta.</p>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
function Pricing() {
  const tiers = [
    { name: "Solo", price: "$0", period: "free during beta", desc: "For solo trainers", features: ["Unlimited clients", "Unlimited sessions", "Public intake page", "Email reminders"], cta: "Start free trial", primary: false },
    { name: "Studio", price: "$29", period: "/mo", desc: "Most popular", features: ["Everything in Solo", "Up to 5 staff", "Analytics dashboard", "Priority support"], cta: "Start free trial", primary: true },
    { name: "Pack", price: "Custom", period: "talk to us", desc: "For multi-location operations", features: ["Everything in Studio", "Unlimited staff", "Dedicated onboarding", "Custom integrations"], cta: "Book a demo", primary: false },
  ];
  return (
    <Section id="pricing" className="bg-white">
      <FadeIn>
        <Eyebrow>Pricing</Eyebrow>
        <Headline className="max-w-2xl">
          Honest pricing.
          <br />
          <em className="italic text-amber-700">No surprises.</em>
        </Headline>
        <p className="mt-3 text-sm text-slate-500">Locked-in beta pricing — pay nothing until you have your first paying client.</p>
      </FadeIn>
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {tiers.map((t, i) => (
          <FadeIn key={t.name} delay={i * 0.04}>
            <div
              className={`h-full rounded-3xl border p-6 ${
                t.primary ? "border-amber-300 bg-amber-50/30 shadow-xl shadow-amber-100/50" : "border-slate-200 bg-white"
              }`}
            >
              {t.primary && (
                <span className="mb-3 inline-block rounded-full bg-amber-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                  Most popular
                </span>
              )}
              <h3 className="text-lg font-semibold">{t.name}</h3>
              <p className="text-xs text-slate-500">{t.desc}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold tabular-nums" style={{ fontFamily: FRAUNCES }}>{t.price}</span>
                <span className="text-sm text-slate-500">{t.period}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-emerald-600" /> {f}
                  </li>
                ))}
              </ul>
              <a href="#cta">
                <Button
                  className={`mt-6 w-full rounded-2xl py-5 ${
                    t.primary ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-900 hover:bg-slate-800"
                  }`}
                >
                  {t.cta}
                </Button>
              </a>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
function FAQ() {
  const items = [
    { q: "Is there really a free trial?", a: "Yes — 14 days, full access, no credit card required. During private beta the Solo tier is permanently free." },
    { q: "How long does setup take?", a: "Most trainers are live in under 15 minutes. Add yourself as the admin, paste in your existing client list, and share the intake link." },
    { q: "Do my clients need an account?", a: "Never. The public intake form is a single shareable URL — no signup needed for dog owners." },
    { q: "Will I lose data when I switch from spreadsheets?", a: "No. CSV import on day one, CSV export any time. Your data is always yours." },
    { q: "Can I use my own email domain for reminders?", a: "Yes. Bring your Resend (or compatible) sending domain and we'll wire it up — owners see your brand, not ours." },
    { q: "What about payments — do you take a cut?", a: "No. We don't process payments. The ledger is for tracking only; Stripe integration ships shortly." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" className="border-t border-slate-100">
      <div className="grid gap-12 md:grid-cols-[1fr_1.5fr]">
        <FadeIn>
          <Eyebrow>Common questions</Eyebrow>
          <Headline>
            Things trainers
            <br />
            <em className="italic text-amber-700">ask us first.</em>
          </Headline>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="divide-y divide-slate-200">
            {items.map((it, i) => {
              const isOpen = open === i;
              return (
                <button
                  key={it.q}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="block w-full py-4 text-left"
                  data-testid={`faq-${i}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-slate-900">{it.q}</span>
                    <ChevronDown
                      size={18}
                      className={`flex-shrink-0 text-slate-400 transition ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                  {isOpen && (
                    <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-600">{it.a}</p>
                  )}
                </button>
              );
            })}
          </div>
        </FadeIn>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
function FinalCTA() {
  return (
    <Section id="cta" className="bg-slate-900 text-white">
      <FadeIn>
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>
            <span className="text-amber-400">Ready to take it for a walk?</span>
          </Eyebrow>
          <h2
            className="text-4xl font-medium leading-tight tracking-tight md:text-6xl"
            style={{ fontFamily: FRAUNCES }}
          >
            Spend less time on admin.
            <br />
            <em className="italic text-amber-300">More time with dogs.</em>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-slate-300">
            Book a 20-minute live demo with the founder — or jump straight in
            with a 14-day free trial.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row" data-testid="final-ctas">
            <Link to="/demo" data-testid="final-try-demo">
              <Button className="rounded-full bg-amber-500 px-7 py-6 text-base text-slate-900 hover:bg-amber-400">
                <Trophy size={16} className="mr-2" />
                Try the live demo
              </Button>
            </Link>
            <a href="mailto:support@pawbookhq.com?subject=Book%20a%20setup%20call" data-testid="final-book-demo">
              <Button variant="outline" className="rounded-full border-slate-700 bg-transparent px-7 py-6 text-base text-white hover:bg-slate-800">
                Book a setup call
              </Button>
            </a>
            <Link to="/intake" data-testid="final-intake">
              <Button variant="outline" className="rounded-full border-slate-700 bg-transparent px-7 py-6 text-base text-white hover:bg-slate-800">
                Just looking for training?
              </Button>
            </Link>
          </div>
          {/* Visible contact email + copy button — for users without a configured
              local mail client (Gmail/web, mobile browser). */}
          <div className="mt-5 flex flex-col items-center gap-2">
            <p className="text-xs text-slate-400">Prefer Gmail or no mail app? Email us directly:</p>
            <ContactEmailBlock
              email="support@pawbookhq.com"
              variant="light"
              subject="Book a setup call"
              data-testid="final-contact-email"
            />
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1.5"><Clock size={11} /> Demo runs in seconds</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={11} /> No signup required</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={11} /> Sandboxed sample data</span>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-[#fdf8f3]">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-5 pb-24 pt-8 text-sm text-slate-500 md:flex-row md:items-center md:px-8 md:pb-20">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-white">
            <PawPrint size={13} />
          </div>
          <span className="font-semibold text-slate-700">Pawbook</span>
          <span>· © {new Date().getFullYear()}</span>
        </div>
        <div className="flex flex-wrap items-center gap-5">
          <a href="#features" className="hover:text-slate-900">Features</a>
          <a href="#pricing" className="hover:text-slate-900">Pricing</a>
          <a href="#faq" className="hover:text-slate-900">FAQ</a>
          <Link to="/intake" className="hover:text-slate-900">Owner intake</Link>
          <Link to="/login" className="hover:text-slate-900">Login</Link>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
function FadeIn({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay }}
    >
      {children}
    </motion.div>
  );
}
